import React, { useState } from 'react';
import { Search, Printer, Download } from 'lucide-react';
import { mockGroups, mockUsers } from '../../data/mockData';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const GroupReport = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useState({
    groupName: '',
    date: new Date().toISOString().slice(0, 10)
  });
  const [searchResults, setSearchResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);

  const handleInputChange = (field, value) => {
    setSearchParams(prev => ({ ...prev, [field]: value }));
  };

  const handleSearch = () => {
    if (!searchParams.groupName.trim()) {
      toast.error(t('groupReport.enterGroupName'));
      return;
    }

    // Mock search logic
    const safeLower = (v) => (v ?? '').toString().toLowerCase();

    const filteredGroups = mockGroups.filter(group =>
      safeLower(group.name).includes(safeLower(searchParams.groupName))
    );

    // Generate mock report data for each group
    const results = filteredGroups.map(group => {
      const groupUsers = mockUsers.filter(user => user.groupId === group.id);
      const totalAmount = groupUsers.reduce((sum, user) => sum + user.amount, 0);
      const totalUsers = groupUsers.length;
      
      return {
        ...group,
        users: groupUsers,
        totalAmount,
        totalUsers,
        reportDate: searchParams.date
      };
    });

    setSearchResults(results);
    setHasSearched(true);
    toast.success(t('groupReport.foundGroups', { count: results.length }));
  };

  const handlePrint = () => {
    toast.info(t('groupReport.printComingSoon'));
  };

  const handleExport = () => {
    toast.info(t('groupReport.exportComingSoon'));
  };

  return (
    <div className="w-full mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-8">
        {t('groupReport.title')}
      </h1>

      <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
          <div className="md:col-span-4">
            <label htmlFor="groupName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('groupReport.groupName')}
            </label>
            <input
              id="groupName"
              type="text"
              value={searchParams.groupName}
              onChange={(e) => handleInputChange('groupName', e.target.value)}
              placeholder={t('groupReport.groupNamePlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
            />
          </div>
          <div className="md:col-span-3">
            <label htmlFor="reportDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('groupReport.reportDate')}
            </label>
            <input
              id="reportDate"
              type="date"
              value={searchParams.date}
              onChange={(e) => handleInputChange('date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
            />
          </div>
          <div className="md:col-span-2">
            <button
              onClick={handleSearch}
              className="w-full inline-flex items-center justify-center px-4 py-2 bg-primary-600 dark:bg-primary-700 text-white font-medium rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors duration-200"
            >
              <Search className="h-4 w-4 mr-2 rtl:mr-0 rtl:ml-2" />
              {t('common.search')}
            </button>
          </div>
          <div className="md:col-span-1.5">
            <button
              onClick={handlePrint}
              disabled={!hasSearched || searchResults.length === 0}
              className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Printer className="h-4 w-4 mr-2 rtl:mr-0 rtl:ml-2" />
              {t('common.print')}
            </button>
          </div>
          <div className="md:col-span-1.5">
            <button
              onClick={handleExport}
              disabled={!hasSearched || searchResults.length === 0}
              className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="h-4 w-4 mr-2 rtl:mr-0 rtl:ml-2" />
              {t('common.export')}
            </button>
          </div>
        </div>
      </div>

      {hasSearched && (
        <>
          {searchResults.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 text-center">
            <h6 className="text-lg font-medium text-gray-500 dark:text-gray-400">
              {t('groupReport.noGroupsFound')}
            </h6>
          </div>
          ) : (
            searchResults.map((group) => (
              <div key={group.id} className="bg-white dark:bg-gray-800 shadow-lg rounded-lg mb-6 p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {group.name}
                  </h2>
                  <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                    group.status === 'active' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                  }`}>
                    {t(`common.${group.status}`)}
                  </span>
                </div>

                {/* Group Summary */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="text-sm font-medium text-primary-600 dark:text-primary-400 mb-2">
                      {t('groupReport.totalUsers')}
                    </div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {group.totalUsers}
                    </div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="text-sm font-medium text-green-600 dark:text-green-400 mb-2">
                      {t('groupReport.totalAmount')}
                    </div>
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                      ${group.totalAmount.toLocaleString()}
                    </div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('groupReport.createdDate')}
                    </div>
                    <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {group.date}
                    </div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('groupReport.reportDate')}
                    </div>
                    <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {group.reportDate}
                    </div>
                  </div>
                </div>

                {/* Group Users Table */}
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  {t('groupReport.groupMembers')}
                </h3>
                <div className="app-table-wrap shadow">
                  <table className="app-table min-w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('groupReport.userName')}</th>
                        <th className="px-6 py-3 text-right rtl:text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('groupReport.amount')}</th>
                        <th className="px-6 py-3 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('common.description')}</th>
                        <th className="px-6 py-3 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('groupReport.joinDate')}</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('common.status')}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {group.users.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">{user.name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 dark:text-green-400 font-medium text-right rtl:text-left">
                            ${user.amount.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{user.description}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{user.joinDate || group.date}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              (user.status || 'active') === 'active' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                            }`}>
                              {t(`common.${user.status || 'active'}`)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {group.notes && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                      {t('common.notes')}:
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {group.notes}
                    </p>
                  </div>
                )}
              </div>
            ))
          )}
        </>
      )}
    </div>
  );
};

export default GroupReport;